import { MaterialIcons } from '@expo/vector-icons';
import React, { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  Pressable,
  ScrollView,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import AppStyles from '@/constants/Styles';
import { useBackgroundOrbat } from '@/hooks/useBackgroundFetch';
import { useThemeColor } from '@/hooks/useThemeColor';
import { OrbatEntry, useNavigationStore } from '@/stores/navigationStore';
import { authenticatedFetch } from '@/utils/auth';

interface GroupedUser {
  type: 'header' | 'user';
  role?: string | null;
  user?: OrbatEntry;
}

// Role hierarchy: Commander > Lieutenant > Sgt Major > Operator > null
const ROLE_HIERARCHY: { [key: string]: number } = {
  'Commander': 4,
  'Lieutenant': 3,
  'Sgt Major': 2,
  'Operator': 1,
  '': 0,
};

function getRoleLevel(role: string | null): number {
  // Normalize to Proper Case for comparison (handle lowercase/underscore variations)
  if (!role) return 0;
  const normalized = role
    .toLowerCase()
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  return ROLE_HIERARCHY[normalized] || 0;
}

function canAssignRole(userRole: string | null, targetRole: string | null): boolean {
  const userLevel = getRoleLevel(userRole);
  const targetLevel = getRoleLevel(targetRole);
  return userLevel >= targetLevel;
}

export default function SettingsPage() {
  const { getSelectedBranch } = useNavigationStore();
  const selectedBranch = getSelectedBranch();
  const insets = useSafeAreaInsets();

  const [selectedUser, setSelectedUser] = useState<OrbatEntry | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // Theme-aware colors
  const tintColor = useThemeColor({}, 'tint');
  const errorColor = useThemeColor({}, 'errorText' as any);
  const cardBackgroundColor = useThemeColor({}, 'cardBackground' as any);
  const iconColor = useThemeColor({}, 'icon');
  const borderColor = useThemeColor({}, 'border' as any);

  // Check if user has permission
  const userRole = selectedBranch?.branch_role || null;
  const hasPermission = userRole && ['Commander', 'Lieutenant'].includes(userRole);

  // Fetch orbat data using background fetch
  const { data: orbat, loading, error, refetch } = useBackgroundOrbat(
    hasPermission && selectedBranch?.branch_short_name ? selectedBranch.branch_short_name : ''
  );

  // Filter out expired entries (only show campaigns whose access has not expired)
  const orbatData = useMemo(() => {
    if (!orbat) return [];
    const now = new Date();
    return orbat.filter(entry => {
      if (!entry.access_expires) return true; // No expiration date means still active
      const expiresDate = new Date(entry.access_expires);
      return expiresDate > now; // Only include if expiration is in the future
    });
  }, [orbat]);

  // Group by user, then select the best entry per user (highest permission, longest expiry)
  // Then group those entries by branch_role for display
  const groupedData = useMemo(() => {
    // First, group all entries by user (using campaigner_id or email as identifier)
    const userGroups = new Map<string, OrbatEntry[]>();
    
    orbatData.forEach(entry => {
      const userId = entry.campaigner_id || entry.email;
      if (!userGroups.has(userId)) {
        userGroups.set(userId, []);
      }
      userGroups.get(userId)!.push(entry);
    });

    // For each user, find the best entry:
    // 1. Highest permission (role level)
    // 2. If same level, longest expiry date
    const bestEntries: OrbatEntry[] = [];
    
    userGroups.forEach((entries) => {
      if (entries.length === 0) return;
      
      // Find entry with highest role level
      const firstEntry = entries[0];
      if (!firstEntry) return; // TypeScript guard
      
      let bestEntry = firstEntry;
      let bestLevel = getRoleLevel(bestEntry.branch_role);
      
      for (const entry of entries) {
        const entryLevel = getRoleLevel(entry.branch_role);
        if (entryLevel > bestLevel) {
          bestEntry = entry;
          bestLevel = entryLevel;
        } else if (entryLevel === bestLevel) {
          // Same level, compare expiry dates (longest expiry wins)
          const bestExpiry = bestEntry.access_expires ? new Date(bestEntry.access_expires).getTime() : 0;
          const entryExpiry = entry.access_expires ? new Date(entry.access_expires).getTime() : 0;
          
          // If bestEntry has no expiry (0) but entry has expiry, entry wins
          // If both have expiry, longer expiry wins
          if (entryExpiry > bestExpiry) {
            bestEntry = entry;
          }
        }
      }
      
      bestEntries.push(bestEntry);
    });

    // Now group the best entries by branch_role for display
    const roleGroups = new Map<string | null, OrbatEntry[]>();
    
    bestEntries.forEach(entry => {
      const role = entry.branch_role || null;
      if (!roleGroups.has(role)) {
        roleGroups.set(role, []);
      }
      roleGroups.get(role)!.push(entry);
    });

    // Sort roles by hierarchy (highest first)
    const sortedRoles = Array.from(roleGroups.keys()).sort((a, b) => {
      return getRoleLevel(b) - getRoleLevel(a);
    });

    const result: GroupedUser[] = [];
    sortedRoles.forEach(role => {
      result.push({ type: 'header', role });
      const users = roleGroups.get(role)!;
      // Sort users by name
      users.sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));
      users.forEach(user => {
        result.push({ type: 'user', user });
      });
    });

    return result;
  }, [orbatData]);

  // Get available roles user can assign
  const availableRoles = useMemo(() => {
    if (!userRole) return [];
    const userLevel = getRoleLevel(userRole);
    return [
      { value: null, label: 'No Role' },
      { value: 'Operator', label: 'Operator' },
      ...(userLevel >= 2 ? [{ value: 'Sgt Major', label: 'Sgt Major' }] : []),
      ...(userLevel >= 3 ? [{ value: 'Lieutenant', label: 'Lieutenant' }] : []),
      ...(userLevel >= 4 ? [{ value: 'Commander', label: 'Commander' }] : []),
    ].filter(role => getRoleLevel(role.value) <= userLevel);
  }, [userRole]);

  const handleUserPress = (user: OrbatEntry) => {
    if (!canAssignRole(userRole, user.branch_role)) {
      return; // Can't modify this user
    }
    setSelectedUser(user);
    // Keep the role in proper case as it comes from the API
    setSelectedRole(user.branch_role);
    setShowRoleModal(true);
  };

  const handleUpdateRole = async () => {
    if (!selectedUser || !selectedBranch) return;

    try {
      setUpdating(true);
      setUpdateError(null);

      // Always revoke current role first
      const revokeResponse = await authenticatedFetch(
        selectedBranch.branch_short_name,
        '/orbat/revoke-role',
        {
          method: 'POST',
          body: JSON.stringify({
            orbat_entry_id: selectedUser.orbat_entry_id,
          }),
        },
        'query'
      );

      if (!revokeResponse.ok) {
        throw new Error(`Failed to revoke role: ${revokeResponse.status}`);
      }

      // If setting to "No Role", we're done after revoking
      if (selectedRole === null) {
        setShowRoleModal(false);
        setSelectedUser(null);
        setSelectedRole(null);
        // Refetch orbat data to get updated state
        await refetch();
        return;
      }

      // Otherwise, assign the new role
      const accessExpires = selectedUser.access_expires || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString();

      const assignResponse = await authenticatedFetch(
        selectedBranch.branch_short_name,
        '/orbat/assign-role',
        {
          method: 'POST',
          body: JSON.stringify({
            campaigner_id: selectedUser.campaigner_id,
            email: selectedUser.email,
            branch_role: selectedRole,
            access_expires: accessExpires,
          }),
        },
        'query'
      );

      if (!assignResponse.ok) {
        throw new Error(`Failed to assign role: ${assignResponse.status}`);
      }

      setShowRoleModal(false);
      setSelectedUser(null);
      setSelectedRole(null);
      // Refetch orbat data to get updated state
      await refetch();
    } catch (err: any) {
      setUpdateError(err.message || 'Failed to update role');
    } finally {
      setUpdating(false);
    }
  };

  if (!hasPermission) {
    return (
      <ThemedView style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <ThemedText style={{ textAlign: 'center', fontSize: 16 }}>
          You do not have permission to access settings.
        </ThemedText>
      </ThemedView>
    );
  }

  if (loading) {
    return (
      <ThemedView style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color={tintColor} />
      </ThemedView>
    );
  }

  if (error) {
    return (
      <ThemedView style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <ThemedText style={{ color: errorColor, textAlign: 'center' }}>
          {error instanceof Error ? error.message : String(error)}
        </ThemedText>
      </ThemedView>
    );
  }

  const renderItem = ({ item }: { item: GroupedUser }) => {
    if (item.type === 'header') {
      const roleLabel = item.role || 'No Role';
      return (
        <View
          style={{
            paddingHorizontal: 16,
            paddingVertical: 12,
            backgroundColor: cardBackgroundColor,
            marginTop: 8,
          }}
        >
          <ThemedText style={[AppStyles.headerText, { fontWeight: '600' }]}>
            {roleLabel}
          </ThemedText>
        </View>
      );
    }

    if (item.type === 'user' && item.user) {
      const user = item.user;
      const canModify = canAssignRole(userRole, user.branch_role);
      
      return (
        <Pressable
          style={[
            AppStyles.listItemLeftAligned,
            { backgroundColor: cardBackgroundColor },
            !canModify && { opacity: 0.5 },
          ]}
          onPress={() => canModify && handleUserPress(user)}
          disabled={!canModify}
        >
          <View style={{ flex: 1 }}>
            <ThemedText style={AppStyles.headerText}>
              {user.name || user.email}
            </ThemedText>
            {user.name && (
              <ThemedText style={{ fontSize: 14, opacity: 0.7, marginTop: 2 }}>
                {user.email}
              </ThemedText>
            )}
            {user.access_expires && (
              <ThemedText style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
                Expires: {new Date(user.access_expires).toLocaleDateString()}
              </ThemedText>
            )}
          </View>
          {canModify && (
            <MaterialIcons name="chevron-right" size={24} color={iconColor} />
          )}
        </Pressable>
      );
    }

    return null;
  };

  const keyExtractor = (item: GroupedUser, index: number) => {
    if (item.type === 'header') {
      return `header-${item.role || 'null'}`;
    }
    if (item.type === 'user' && item.user) {
      return `user-${item.user.campaigner_id}`;
    }
    return `item-${index}`;
  };

  return (
    <ThemedView style={{ flex: 1 }}>
      <FlatList
        data={groupedData}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        contentContainerStyle={{ paddingBottom: insets.bottom }}
      />

      {/* Role Assignment Modal */}
      <Modal
        visible={showRoleModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowRoleModal(false)}
      >
        <TouchableOpacity
          style={AppStyles.modalContainer}
          activeOpacity={1}
          onPress={() => {
            setShowRoleModal(false);
            setUpdateError(null);
          }}
        >
          <View style={[AppStyles.modalView, { backgroundColor: cardBackgroundColor }]}>
            <TouchableOpacity
              style={AppStyles.modalCloseButton}
              onPress={() => {
                setShowRoleModal(false);
                setUpdateError(null);
              }}
              accessibilityLabel="Close dialog"
              accessibilityHint="Closes the role assignment dialog"
            >
              <MaterialIcons name="close" size={20} color={iconColor} />
            </TouchableOpacity>

            <ThemedText style={AppStyles.modalTitle}>
              Assign Role
            </ThemedText>

            {selectedUser && (
              <>
                <ThemedText style={{ fontSize: 16, marginBottom: 8 }}>
                  {selectedUser.name || selectedUser.email}
                </ThemedText>
                <ThemedText style={{ fontSize: 14, opacity: 0.7, marginBottom: 20 }}>
                  {selectedUser.email}
                </ThemedText>
              </>
            )}

            <ScrollView style={{ maxHeight: 300 }}>
              {availableRoles.map(role => {
                // Normalize comparison - handle null and string values
                const isSelected = selectedRole === role.value || 
                  (selectedRole === null && role.value === null) ||
                  (selectedRole && role.value && selectedRole.toLowerCase() === role.value.toLowerCase());
                
                return (
                  <Pressable
                    key={role.value || 'null'}
                    style={[
                      {
                        padding: 16,
                        borderRadius: 8,
                        marginBottom: 8,
                        borderWidth: 1,
                        borderColor: isSelected ? tintColor : borderColor,
                        backgroundColor: isSelected ? tintColor + '20' : 'transparent',
                      },
                    ]}
                    onPress={() => setSelectedRole(role.value)}
                  >
                    <ThemedText style={{ fontSize: 16 }}>
                      {role.label}
                    </ThemedText>
                  </Pressable>
                );
              })}
            </ScrollView>

            {updateError && (
              <ThemedText style={{ color: errorColor, marginTop: 12, textAlign: 'center' }}>
                {updateError}
              </ThemedText>
            )}

            <View style={{ flexDirection: 'row', marginTop: 20, gap: 12 }}>
              <Pressable
                style={[
                  {
                    flex: 1,
                    padding: 16,
                    borderRadius: 8,
                    backgroundColor: borderColor,
                    alignItems: 'center',
                  },
                ]}
                onPress={() => {
                  setShowRoleModal(false);
                  setUpdateError(null);
                }}
                disabled={updating}
              >
                <ThemedText>Cancel</ThemedText>
              </Pressable>
              <Pressable
                style={[
                  {
                    flex: 1,
                    padding: 16,
                    borderRadius: 8,
                    backgroundColor: tintColor,
                    alignItems: 'center',
                  },
                ]}
                onPress={handleUpdateRole}
                disabled={updating}
              >
                {updating ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <ThemedText style={{ color: '#fff' }}>Update</ThemedText>
                )}
              </Pressable>
            </View>
          </View>
        </TouchableOpacity>
      </Modal>
    </ThemedView>
  );
}

